import { useEffect, useRef, useState } from 'react';
import { pendingLabel, type ChatMessageItem, type ChatPayload, type ChatSessionSummary, type LotteryAnswerPayload } from '@todo/shared';
import { chatApi, type ChatHelp, type ChatTurn } from '../api/chat.api';
import { ApiError } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { emitAssistantChange } from '../hooks/useAssistantRefresh';
import { LotteryAnswer } from './chat/LotteryAnswer';
import { ChatMessageText, plainChatText } from './ChatMessageText';
import { CopyButton } from './CopyButton';

/**
 * The AI assistant panel.
 *
 * The button that opens it lives in <AppFooter> - the footer's "Assistant"
 * item owns the bottom-right corner and enforces one open panel at a time,
 * so this component is CONTROLLED: it renders the panel and nothing else.
 * It stays mounted while closed, which is what keeps the conversation alive
 * between openings.
 *
 * Design decisions worth knowing:
 *  - Conversation memory lives on the SERVER (a chat session). The widget
 *    holds only the current sessionId and sends it with every message; it
 *    never sends history. The first message of a new chat creates the
 *    session and the server returns its id.
 *  - The last opened session id is remembered per membership in
 *    localStorage, so a page reload continues the same conversation. If it
 *    is gone, the most recent session is opened; if there is none, the chat
 *    starts fresh.
 *  - Only real turns are stored. The greeting, '/help' and the widget's own
 *    "Sorry - ..." bubbles are display-only and never reach the server.
 *  - STATELESS FALLBACK: if a reply comes back without a sessionId (the
 *    server's chat tables are not installed yet), the widget quietly goes
 *    back to the old behaviour - it replays its recent turns with each
 *    message, as before sessions existed. No error is shown.
 *  - '/help' never reaches the API: the module catalogue comes from
 *    GET /api/chat/help once, and rendering it is a UI concern.
 *  - If the server reports the assistant is not configured, the panel says
 *    so and the input is disabled, rather than silently doing nothing.
 *
 * ONE CHAT FOR EVERYTHING (docs/lottery/CHATBOT_DESIGN.md section 15): no
 * tabs, no mode, no persona. The SERVER decides whether a message is a
 * lottery question and, for someone with access, routes it to the Lottery
 * analyst; its answer arrives in the same thread:
 *  - charts (lazy recharts), tables (Indian formatting), report cards and
 *    the "Figures up to · persona" line render from the payload; the
 *    grounding check is NOT shown
 *  - the panel grows as soon as the thread has a chart or table, with an
 *    expand button; it shrinks back on New chat
 *  - a lottery answer withheld from someone without access shows as a
 *    muted note (the server sends `hidden`)
 *  - the working state is the same for every message (the route is not
 *    known until the reply): a seconds counter from 5 s, a note from 45 s
 *  - 409 (busy), 429, 503 and 403 are shown with the server's own text;
 *    lottery limits and "not set up" arrive as stored notices in the thread
 */

type Message =
  | {
      kind: 'text';
      role: 'user' | 'assistant';
      content: string;
      payload?: ChatPayload | null;
      status?: 'ok' | 'error';
      /** A lottery answer withheld from someone without lottery access. */
      hidden?: boolean;
      /** A server refusal shown as a notice (busy, limits, not set up, access). */
      notice?: boolean;
    }
  | { kind: 'help' };

function greeting(lottery: boolean): Message {
  return {
    kind: 'text',
    role: 'assistant',
    content: lottery
      ? "Hi! I can create tasks and sub-tasks, move them on the board, star them, change assignees, manage your calendar and lists, and look up people - and answer questions about lottery sales, draws, stockists, locations and regions, with charts and tables.\n\nType /help to see examples for each module."
      : "Hi! I can create tasks and sub-tasks, move them on the board, star them, change assignees, manage your calendar and lists, and look up people.\n\nType /help to see examples for each module.",
  };
}

/** How many stored turns to show when a session opens. */
const HISTORY_PAGE = 50;
const SESSIONS_PAGE = 20;
/** The working counter appears after this long (quick answers do not need it). */
const COUNTER_SECONDS = 5;
/** After this long, the working note says complex questions can take a minute. */
const SLOW_NOTE_SECONDS = 45;

/** localStorage, per membership (users.id is per organisation). */
const lastSessionKey = (userId: string) => `todo.chat.lastSession.${userId}`;
/** Left by the old "Lottery analyst" tab (Phase 8 before section 15) - cleared on sight. */
const oldLotteryKey = (userId: string) => `todo.chat.lastSession.${userId}.lottery`;

/** Storage can throw (private mode, quota) - remembering is a nicety. */
function store(key: string, value: string | null): void {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // Not remembered.
  }
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Stateless mode only: the recent turns, replayed as before sessions existed. */
function historyFor(list: Message[]): ChatTurn[] {
  return list
    .filter((m): m is Extract<Message, { kind: 'text' }> => m.kind === 'text' && !m.notice)
    .slice(-12)
    .map(({ role, content }) => ({ role, content }));
}

function toMessages(items: ChatMessageItem[]): Message[] {
  return items.map((m) => ({
    kind: 'text', role: m.role, content: m.content, payload: (m.payload ?? null) as ChatPayload | null, status: m.status, hidden: m.hidden === true,
  }));
}

const lotteryAnswerOf = (m: Message): LotteryAnswerPayload | null =>
  m.kind === 'text' && m.payload?.kind === 'lottery_answer' ? (m.payload as LotteryAnswerPayload) : null;

/** Does the thread hold a chart or a table? Then the panel grows. */
const hasWideBlock = (list: Message[]) =>
  list.some((m) => lotteryAnswerOf(m)?.blocks.some((b) => b.type === 'chart' || b.type === 'table') ?? false);

/** "Today 14:05", "12 Sep" - enough to tell sessions apart. */
function formatWhen(iso: string): string {
  const date = new Date(iso);
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? `Today ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : date.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

/** The server's own text for the refusals worth showing as they are. */
function refusalText(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (![403, 409, 429, 503].includes(error.status)) return null;
  const retry = (error.details as { retryAfterSeconds?: unknown } | undefined)?.retryAfterSeconds;
  return typeof retry === 'number' && error.status === 429 && !/midnight/i.test(error.message)
    ? `${error.message} Try again in ${retry} seconds.`
    : error.message;
}

export function ChatWidget({
  open, onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const userId = user?.id;
  const [help, setHelp] = useState<ChatHelp | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([greeting(false)]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  /** The server answered without a sessionId - replay history ourselves. */
  const [stateless, setStateless] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [helpModule, setHelpModule] = useState<string | null>(null);
  /** 'loading' until the remembered / most recent session has been fetched. */
  const [historyState, setHistoryState] = useState<'idle' | 'loading' | 'ready'>('idle');
  const [view, setView] = useState<'chat' | 'sessions'>('chat');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const lotteryAvailable = help?.lottery?.available === true;

  const loadHelp = () =>
    chatApi
      .help()
      .then(setHelp)
      .catch(() => setHelp({ enabled: false, modules: [] }));

  useEffect(() => {
    void loadHelp();
  }, []);

  // The greeting mentions lottery questions once we know they are answered.
  useEffect(() => {
    setMessages((prev) => (prev[0]?.kind === 'text' && prev[0].role === 'assistant' && !prev[0].payload
      ? [greeting(lotteryAvailable), ...prev.slice(1)]
      : prev));
  }, [lotteryAvailable]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, open, helpModule, view, busy]);

  // The working timer (a routed lottery answer can take tens of seconds).
  useEffect(() => {
    if (!busy) return;
    setElapsed(0);
    const started = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(t);
  }, [busy]);

  /** Show one stored session (its most recent page of turns). */
  const openSession = async (id: string): Promise<boolean> => {
    try {
      const page = await chatApi.messages(id, { pageSize: HISTORY_PAGE });
      setSessionId(id);
      setStateless(false);
      setMessages([greeting(lotteryAvailable), ...toMessages(page.data)]);
      setHelpModule(null);
      if (userId) store(lastSessionKey(userId), id);
      return true;
    } catch {
      return false;
    }
  };

  const startNewChat = () => {
    setSessionId(null);
    setStateless(false);
    setMessages([greeting(lotteryAvailable)]);
    setHelpModule(null);
    setInput('');
    setView('chat');
    setExpanded(false);
    if (userId) store(lastSessionKey(userId), null);
    inputRef.current?.focus();
  };

  /** Continue where the person left off: remembered, else most recent, else fresh. */
  const restore = async () => {
    if (!userId) return;
    setHistoryState('loading');
    store(oldLotteryKey(userId), null);
    const remembered = read(lastSessionKey(userId));
    if (remembered && (await openSession(remembered))) {
      setHistoryState('ready');
      return;
    }
    store(lastSessionKey(userId), null);
    try {
      const recent = await chatApi.sessions({ pageSize: 1 });
      const latest = recent.data[0];
      if (latest) await openSession(latest.id);
    } catch {
      // No history available - the fresh greeting stays.
    }
    setHistoryState('ready');
  };

  /**
   * First open: continue where the person left off. Once per mount (the
   * widget stays mounted while closed).
   */
  useEffect(() => {
    if (!open || historyState !== 'idle' || !userId) return;
    void restore();
    // restore is recreated each render; running once per open is intended.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, historyState, userId]);

  // The panel ALWAYS opens, so the UI is verifiable before the Azure
  // OpenAI keys exist. When the server reports the assistant unconfigured,
  // the panel says so and the input is disabled instead of hiding the
  // whole widget.
  const configured = help === null || help.enabled;
  const loadingHistory = historyState === 'loading';

  const send = async (raw?: string) => {
    const text = (raw ?? input).trim();
    if (text === '' || busy || !configured || loadingHistory) return;
    setInput('');

    if (text === '/help' || text === 'help' && messages.length === 1) {
      setHelpModule(null);
      setMessages((prev) => [
        ...prev,
        { kind: 'text', role: 'user', content: text },
        { kind: 'help' },
      ]);
      return;
    }

    const before = messages;
    setMessages((prev) => [...prev, { kind: 'text', role: 'user', content: text }]);
    setBusy(true);
    try {
      const { reply, changed, sessionId: stored, payload } = await chatApi.send(
        text,
        sessionId,
        stateless ? historyFor(before) : [],
      );
      if (stored) {
        setStateless(false);
        if (stored !== sessionId) {
          setSessionId(stored);
          if (userId) store(lastSessionKey(userId), stored);
        }
      } else {
        // Not stored (chat tables not installed on the server): carry on
        // statelessly, exactly as before sessions existed. No error shown.
        setStateless(true);
        if (sessionId !== null) {
          setSessionId(null);
          if (userId) store(lastSessionKey(userId), null);
        }
      }
      setMessages((prev) => [...prev, { kind: 'text', role: 'assistant', content: reply, payload: payload ?? null }]);
      // A write happened server-side: tell whatever page is open to refetch.
      if (changed) emitAssistantChange();
    } catch (error) {
      // The session was deleted elsewhere: the next message starts a new one.
      const gone = error instanceof ApiError && error.status === 404 && sessionId !== null;
      if (gone) {
        setSessionId(null);
        if (userId) store(lastSessionKey(userId), null);
      }
      // Access removed mid-answer: refresh what the widget offers.
      if (error instanceof ApiError && error.status === 403) void loadHelp();
      const refusal = refusalText(error);
      setMessages((prev) => [
        ...prev,
        {
          kind: 'text',
          role: 'assistant',
          notice: refusal !== null,
          content: gone
            ? 'Sorry - this chat no longer exists. Send your message again to start a new one.'
            : refusal
              ?? (error instanceof Error && error.message
                ? `Sorry - ${error.message}`
                : 'Sorry - something went wrong. Please try again.'),
        },
      ]);
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const pickExample = (example: string) => {
    setInput(example);
    inputRef.current?.focus();
  };

  /** The question just before a failed answer, for "Ask again". */
  const questionBefore = (index: number): string | null => {
    for (let i = index - 1; i >= 0; i -= 1) {
      const m = messages[i]!;
      if (m.kind === 'text' && m.role === 'user') return m.content;
    }
    return null;
  };

  const wide = hasWideBlock(messages);
  const size = !wide
    ? 'h-[560px] w-96'
    : expanded
      ? 'h-[calc(100vh-7rem)] w-[min(1100px,calc(100vw-2rem))]'
      : 'h-[min(80vh,760px)] w-[min(760px,calc(100vw-2rem))]';
  const helpModules = [
    ...(help?.modules ?? []),
    ...(lotteryAvailable
      ? [{ module: 'Lottery', description: 'Questions about lottery sales, draws, stockists, locations and regions - answered with figures, charts and tables', examples: help?.lottery?.examples ?? [] }]
      : []),
  ];

  return (
    <>
      {/* Anchored just above the footer bar (44px) that opens it. */}
      {open && (
        <div
          role="dialog"
          aria-label="Charles Group Assistant"
          className={`fixed bottom-14 right-4 z-30 flex ${size} max-h-[calc(100vh-7rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl`}
        >
          <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-900 px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white">Charles Group Assistant</p>
              <p className="text-xs text-slate-300">
                {view === 'sessions' ? 'Your past chats' : 'Type /help for what I can do'}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {wide && (
                <HeaderButton
                  label={expanded ? 'Make the panel smaller' : 'Make the panel larger'}
                  active={expanded}
                  onClick={() => setExpanded((e) => !e)}
                  path={expanded ? 'M8 4v4H4M12 4v4h4M8 16v-4H4M12 16v-4h4' : 'M4 8V4h4M16 8V4h-4M4 12v4h4M16 12v4h-4'}
                />
              )}
              <HeaderButton
                label={view === 'sessions' ? 'Back to the chat' : 'Past chats'}
                active={view === 'sessions'}
                onClick={() => setView((v) => (v === 'sessions' ? 'chat' : 'sessions'))}
                path="M4 5h12M4 10h12M4 15h8"
              />
              <HeaderButton label="New chat" onClick={startNewChat} path="M10 4v12M4 10h12" />
              <HeaderButton label="Close the assistant" onClick={onClose} path="M5 5l10 10M15 5L5 15" />
            </div>
          </div>

          {!configured && (
            <div className="border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              The assistant is not configured on this server yet. Set the
              AZURE_OPENAI_* values in apps/api/.env and restart the API to
              enable chatting.
            </div>
          )}

          {view === 'sessions' ? (
            <SessionList
              currentId={sessionId}
              onOpen={async (id) => {
                if (await openSession(id)) setView('chat');
              }}
              onDeleted={(id) => {
                if (id === sessionId) startNewChat();
              }}
              onNewChat={startNewChat}
            />
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
                {messages.map((m, i) => {
                  if (m.kind === 'help') {
                    return (
                      <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Pick a module
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {helpModules.map((mod) => (
                            <button
                              key={mod.module}
                              type="button"
                              aria-pressed={helpModule === mod.module}
                              onClick={() =>
                                setHelpModule((cur) => (cur === mod.module ? null : mod.module))
                              }
                              className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                                helpModule === mod.module
                                  ? 'border-slate-900 bg-slate-900 text-white'
                                  : 'border-slate-300 bg-white text-slate-700 hover:border-slate-500'
                              }`}
                            >
                              {mod.module}
                            </button>
                          ))}
                        </div>
                        {helpModule && (
                          <div className="mt-3 space-y-1.5">
                            <p className="text-xs text-slate-500">
                              {helpModules.find((mod) => mod.module === helpModule)?.description}.
                              Tap an example to put it in the box, edit, then send:
                            </p>
                            {helpModules
                              .find((mod) => mod.module === helpModule)
                              ?.examples.map((ex) => (
                                <button
                                  key={ex}
                                  type="button"
                                  onClick={() => pickExample(ex)}
                                  className="block w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-left text-xs text-slate-700 transition hover:border-blue-400 hover:bg-blue-50"
                                >
                                  {ex}
                                </button>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  const answer = lotteryAnswerOf(m);
                  const storedNotice = m.payload?.kind === 'notice';
                  return (
                    <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                      <div className={m.role === 'user' ? 'max-w-[85%]' : answer ? 'w-full max-w-[95%]' : 'max-w-[85%]'}>
                        <div
                          role={m.notice ? 'alert' : m.hidden ? 'note' : undefined}
                          className={`whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm ${
                            m.role === 'user'
                              ? 'rounded-br-sm bg-blue-600 text-white'
                              : m.notice || storedNotice
                                ? 'rounded-bl-sm border border-amber-200 bg-amber-50 text-amber-900'
                                : m.hidden
                                  ? 'rounded-bl-sm border border-dashed border-slate-300 bg-white text-xs text-slate-500 italic'
                                  : m.status === 'error'
                                    ? 'rounded-bl-sm bg-slate-50 text-slate-500 italic'
                                    : 'rounded-bl-sm bg-slate-100 text-slate-800'
                          }`}
                        >
                          {m.role === 'assistant' ? <ChatMessageText text={m.content} /> : m.content}
                        </div>
                        {m.role === 'assistant' && i > 0 && !m.hidden && (
                          <div className="mt-1 flex justify-end">
                            <CopyButton text={plainChatText(m.content)} />
                          </div>
                        )}
                        {answer && <LotteryAnswer payload={answer} />}
                        {m.status === 'error' && questionBefore(i) && (
                          <button
                            type="button"
                            onClick={() => pickExample(questionBefore(i)!)}
                            className="mt-1 text-xs font-medium text-blue-700 hover:underline"
                          >
                            Ask again
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {loadingHistory && (
                  <p className="text-center text-xs text-slate-400">Loading your last chat...</p>
                )}
                {busy && (
                  <div className="flex justify-start" role="status" aria-live="polite">
                    <div className="rounded-2xl rounded-bl-sm bg-slate-100 px-3 py-2 text-sm text-slate-500">
                      {pendingLabel([...messages].reverse().find((m): m is Extract<Message, { kind: 'text' }> => m.kind === 'text' && m.role === 'user')?.content)}
                      {elapsed >= COUNTER_SECONDS && <span className="ml-1 tabular-nums">{elapsed} s</span>}
                      {elapsed >= SLOW_NOTE_SECONDS && (
                        <span className="block text-xs">Still working - complex questions can take up to a minute.</span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-200 p-2">
                <div className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        void send();
                      }
                    }}
                    rows={2}
                    disabled={!configured}
                    aria-label="Message the assistant"
                    placeholder={!configured ? 'Assistant not configured' : 'e.g. "Mark CG-42 as done"  ·  /help'}
                    className="min-h-[2.5rem] flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => void send()}
                    disabled={busy || loadingHistory || input.trim() === '' || !configured}
                    aria-label="Send"
                    className="rounded-xl bg-blue-600 p-2.5 text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-5 w-5">
                      <path d="M3 10l13-6-4 6 4 6-13-6z" fill="currentColor" />
                    </svg>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

    </>
  );
}

/** One icon button in the dark header strip. */
function HeaderButton({
  label, path, onClick, active,
}: {
  label: string;
  path: string;
  onClick: () => void;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-pressed={active}
      className={`rounded-lg p-1.5 transition hover:bg-slate-700 hover:text-white ${
        active ? 'bg-slate-700 text-white' : 'text-slate-300'
      }`}
    >
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-5 w-5">
        <path d={path} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </button>
  );
}

/**
 * The person's past chats - one list, task and lottery questions alike:
 * open, rename, delete. Loaded fresh each time the list is shown; "Show
 * more" pages through older ones.
 */
function SessionList({
  currentId, onOpen, onDeleted, onNewChat,
}: {
  currentId: string | null;
  onOpen: (id: string) => Promise<void>;
  onDeleted: (id: string) => void;
  onNewChat: () => void;
}) {
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameText, setRenameText] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [workingId, setWorkingId] = useState<string | null>(null);

  const load = async (nextPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await chatApi.sessions({ page: nextPage, pageSize: SESSIONS_PAGE });
      setSessions((prev) => (nextPage === 1 ? result.data : [...prev, ...result.data]));
      setPage(nextPage);
      setTotalPages(result.meta?.totalPages ?? 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your chats.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveRename = async (id: string) => {
    const title = renameText.trim();
    if (!title) return;
    setWorkingId(id);
    try {
      const renamed = await chatApi.rename(id, title);
      setSessions((prev) => prev.map((s) => (s.id === id ? renamed : s)));
      setRenamingId(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not rename the chat.');
    } finally {
      setWorkingId(null);
    }
  };

  const remove = async (id: string) => {
    setWorkingId(id);
    try {
      await chatApi.remove(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      setConfirmDeleteId(null);
      onDeleted(id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete the chat.');
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-3 py-3">
      <button
        type="button"
        onClick={onNewChat}
        className="mb-3 w-full rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700"
      >
        + New chat
      </button>

      {error && (
        <p role="alert" className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-red-100">
          {error}
        </p>
      )}

      {!loading && sessions.length === 0 && !error && (
        <p className="py-6 text-center text-xs text-slate-500">No past chats yet.</p>
      )}

      <ul className="space-y-1.5">
        {sessions.map((s) => (
          <li
            key={s.id}
            className={`rounded-lg border px-2.5 py-2 ${
              s.id === currentId ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white'
            }`}
          >
            {renamingId === s.id ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void saveRename(s.id);
                }}
                className="flex items-center gap-1.5"
              >
                <input
                  autoFocus
                  value={renameText}
                  maxLength={120}
                  onChange={(e) => setRenameText(e.target.value)}
                  aria-label="Chat name"
                  className="min-w-0 flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm outline-none focus:border-blue-500"
                />
                <button
                  type="submit"
                  disabled={workingId === s.id || renameText.trim() === ''}
                  className="rounded-md bg-blue-600 px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setRenamingId(null)}
                  className="rounded-md px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
                >
                  Cancel
                </button>
              </form>
            ) : confirmDeleteId === s.id ? (
              <div className="flex items-center justify-between gap-2">
                <p className="min-w-0 truncate text-xs text-slate-700">Delete "{s.title}"?</p>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    disabled={workingId === s.id}
                    onClick={() => void remove(s.id)}
                    className="rounded-md bg-red-600 px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(null)}
                    className="rounded-md px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
                  >
                    Keep
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void onOpen(s.id)}
                  className="min-w-0 flex-1 text-left"
                >
                  <span className="block truncate text-sm text-slate-800">{s.title}</span>
                  <span className="block text-[11px] text-slate-500">{formatWhen(s.updatedAt)}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRenameText(s.title);
                    setRenamingId(s.id);
                  }}
                  className="rounded-md px-1.5 py-1 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                >
                  Rename
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDeleteId(s.id)}
                  className="rounded-md px-1.5 py-1 text-xs text-slate-500 hover:bg-red-50 hover:text-red-700"
                >
                  Delete
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {loading && <p className="py-3 text-center text-xs text-slate-400">Loading...</p>}
      {!loading && page < totalPages && (
        <button
          type="button"
          onClick={() => void load(page + 1)}
          className="mt-2 w-full rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
        >
          Show more
        </button>
      )}
    </div>
  );
}
